import { useState } from 'react';
import type {
  DepartmentTask,
  PlanCategory,
  ProductionDepartment,
  ProductionRequirementView,
  RequirementEdit,
} from '@scoutaround/script-breakdown-shared';

import { api } from '../../lib/api.js';
import { useRefusal } from '../../lib/permissions.js';
import { POINTED_AT_STYLE, useScrollIntoViewWhen } from '../../lib/useScrollIntoViewWhen.js';
import { Disclosure } from '../ui/Disclosure.js';
import { MoreMenu, type MenuItem } from '../ui/MoreMenu.js';
import { StatusPill } from '../ui/StatusPill.js';
import { usePlan, usePlanWrite, writeError } from './context.js';
import { DerivationInspector } from './DerivationInspector.js';
import {
  APPLICABILITY_LABELS,
  BAND_LABELS,
  COMPARISON_LABELS,
  DEPARTMENTS,
  DEPARTMENT_LABELS,
  NECESSITY_LABELS,
  ORIGIN_HINTS,
  ORIGIN_LABELS,
  PLAN_CATEGORIES,
  QUANTITY_SOURCE_LABELS,
  REQUIREMENT_TYPE_LABELS,
  REVIEW_STATE,
  RISK_LABELS,
  categoryLabel,
} from './labels.js';
import { EvidenceQuotes } from './PlanParts.js';
import { RevalidationDiff } from './RevalidationDiff.js';

type Decision = { action: 'ACCEPT' | 'REJECT' | 'RESTORE' } | { action: 'EDIT'; edit: RequirementEdit };

/**
 * ONE REQUIREMENT OF THE PLAN, with everything that says why it is there.
 *
 * Level 1 on the row: the name, where it comes from (Explicit, Derived,
 * Suggested, Manual), the decision, and whether it applies once the methods are
 * known — in words. Level 2 beside it: necessity and its condition, the AI's
 * count as a hint and the approved count apart from it, the confidence, the risk
 * flags. Level 3 on demand: the quotes and the derivation.
 *
 * Decisions sit in the row's ⋯ menu, never as a red button: the screen has one.
 * A row whose basis moved shows the change instead of Accept and Reject, which
 * the server refuses on it.
 */
export function RequirementRow({
  requirement,
  sceneId,
  pinned = false,
}: {
  requirement: ProductionRequirementView;
  /** The scene the row is shown on: its quotes come first. */
  sceneId?: string;
  /** A potential safety review: never dimmed, whatever the method. */
  pinned?: boolean;
}) {
  const { projectId, focusRequirementId } = usePlan();
  const refusal = useRefusal('SCRIPT_REVIEW');
  const [editing, setEditing] = useState(false);
  const id = requirement.id;
  const pointed = focusRequirementId === id;
  const ref = useScrollIntoViewWhen<HTMLLIElement>(pointed);

  const decide = usePlanWrite((input: Decision) =>
    api.decidePlanRequirement(projectId, id, { ...input, rowVersion: requirement.rowVersion }),
  );

  const status = REVIEW_STATE[requirement.reviewStatus];
  const revalidating = requirement.reviewStatus === 'NEEDS_REVALIDATION';
  const blocked = decide.isPending || refusal !== null;

  const items: MenuItem[] = [
    ...(requirement.reviewStatus === 'PENDING'
      ? [
          { id: 'accept', label: 'Accept', testId: `production-requirement-accept-${id}`, onSelect: () => decide.mutate({ action: 'ACCEPT' }) },
          { id: 'reject', label: 'Reject', tone: 'danger' as const, testId: `production-requirement-reject-${id}`, onSelect: () => decide.mutate({ action: 'REJECT' }) },
        ]
      : []),
    ...(requirement.reviewStatus === 'ACCEPTED'
      ? [{ id: 'reject', label: 'Reject', tone: 'danger' as const, testId: `production-requirement-reject-${id}`, onSelect: () => decide.mutate({ action: 'REJECT' }) }]
      : []),
    ...(requirement.reviewStatus === 'REJECTED'
      ? [{ id: 'restore', label: 'Restore', testId: `production-requirement-restore-${id}`, onSelect: () => decide.mutate({ action: 'RESTORE' }) }]
      : []),
    {
      id: 'edit',
      label: 'Edit name, category, departments or count',
      testId: `production-requirement-edit-${id}`,
      onSelect: () => {
        setEditing(true);
      },
    },
  ].map((item) => (blocked ? { ...item, disabled: true, hint: refusal ?? 'Saving…' } : item));

  const here = requirement.scenes.find((s) => s.sceneId === sceneId);
  const evidence = [
    ...(here?.evidence ?? []),
    ...requirement.scenes.filter((s) => s.sceneId !== sceneId).flatMap((s) => s.evidence),
  ];
  const quantity = requirement.quantity;
  const hasNotes =
    requirement.specialistReview ||
    requirement.regulatoryCheck !== null ||
    (requirement.comparison !== null && requirement.comparison !== 'UNCHANGED') ||
    (requirement.previous?.decision ?? null) !== null;

  return (
    <li
      ref={ref}
      className="pp-req"
      data-testid={`production-requirement-${id}`}
      data-applicability={requirement.effectiveApplicability}
      data-pinned={pinned ? '1' : undefined}
      data-pointed={pointed ? '1' : undefined}
      style={pointed ? POINTED_AT_STYLE : undefined}
    >
      <div className="pp-req-head">
        <span className="pp-req-name">{requirement.name}</span>
        <span
          className="pp-tag"
          data-origin={requirement.origin}
          title={ORIGIN_HINTS[requirement.origin]}
          data-testid={`production-requirement-origin-${id}`}
        >
          {ORIGIN_LABELS[requirement.origin]}
        </span>
        <StatusPill state={status.state} label={status.label} size="sm" testId={`production-requirement-status-${id}`} />
        <span className="ui-toolbar-end">
          <MoreMenu items={items} testId={`production-requirement-more-${id}`} label={`Actions for ${requirement.name}`} />
        </span>
      </div>

      <p className="pp-req-line">
        <span data-testid={`production-requirement-applicability-${id}`} className="pp-applicability">
          {APPLICABILITY_LABELS[requirement.effectiveApplicability]}
        </span>
        <span data-testid={`production-requirement-necessity-${id}`}>
          {` · ${NECESSITY_LABELS[requirement.necessity]}`}
          {requirement.condition === null ? '' : `: ${requirement.condition.text}`}
        </span>
        <span className="mm">{` · ${REQUIREMENT_TYPE_LABELS[requirement.requirementType]} · ${BAND_LABELS[requirement.band]}`}</span>
      </p>

      {here === undefined || here.need === '' ? null : <p className="pp-req-need">{here.need}</p>}

      <p className="pp-req-line mm">
        <span data-testid={`production-requirement-ai-count-${id}`}>
          {/* No number is "not stated", never 0. */}
          {quantity.value === null
            ? 'AI count: not stated'
            : `AI count (a hint): ${String(quantity.value)}, ${QUANTITY_SOURCE_LABELS[quantity.source]}`}
        </span>
        <span data-testid={`production-requirement-approved-count-${id}`}>
          {requirement.approvedQuantity === null
            ? ' · No approved count'
            : ` · Approved count: ${String(requirement.approvedQuantity)}`}
        </span>
      </p>

      {requirement.departments.length === 0 && requirement.riskFlags.length === 0 ? null : (
        <div className="pp-chips">
          {requirement.departments.map((d) => (
            <span key={d.department} className="chip" title={d.task === '' ? undefined : d.task}>
              {`${DEPARTMENT_LABELS[d.department]}${d.primary ? ' (lead)' : ''}`}
            </span>
          ))}
          {requirement.riskFlags.map((flag) => (
            <span key={flag} className="pp-risk" data-testid={`production-requirement-risk-${id}-${flag}`}>
              {RISK_LABELS[flag]}
            </span>
          ))}
        </div>
      )}

      {!hasNotes ? null : (
        <ul className="pp-req-notes">
          {requirement.specialistReview ? <li>A specialist should review this. The plan records that a review is needed, not its outcome.</li> : null}
          {requirement.regulatoryCheck === null ? null : (
            <li>{`May depend on the rules where you shoot: ${requirement.regulatoryCheck.note}`}</li>
          )}
          {requirement.comparison === null || requirement.comparison === 'UNCHANGED' ? null : (
            <li data-testid={`production-requirement-comparison-${id}`}>{COMPARISON_LABELS[requirement.comparison]}</li>
          )}
          {/* What was decided on the previous draft is information, never a decision carried over. */}
          {requirement.previous === null || requirement.previous.decision === null ? null : (
            <li data-testid={`production-requirement-previous-${id}`}>
              {`${requirement.previous.decision === 'ACCEPTED' ? 'Accepted' : 'Rejected'} in the previous draft${requirement.previous.approvedQuantity === null ? '' : `, count ${String(requirement.previous.approvedQuantity)}`}.`}
              {requirement.reviewStatus === 'PENDING' ? ' Not carried over: decide it for this draft.' : null}
            </li>
          )}
        </ul>
      )}

      {revalidating ? <RevalidationDiff requirement={requirement} disabledReason={refusal} /> : null}

      {editing ? (
        <RequirementEditForm
          requirement={requirement}
          busy={decide.isPending}
          onCancel={() => {
            setEditing(false);
          }}
          onSave={(edit) => {
            decide.mutate(
              { action: 'EDIT', edit },
              {
                onSuccess: () => {
                  setEditing(false);
                },
              },
            );
          }}
        />
      ) : null}

      {decide.error === null ? null : (
        <p className="pp-error" role="alert" data-testid={`production-requirement-error-${id}`}>
          {writeError(decide.error)}
        </p>
      )}

      <div className="pp-req-more">
        <Disclosure
          summary="Evidence"
          meta={evidence.length === 0 ? undefined : `${String(evidence.length)} quote${evidence.length === 1 ? '' : 's'}`}
          testId={`production-requirement-evidence-${id}`}
        >
          <EvidenceQuotes spans={evidence} sceneId={sceneId} testId={`production-requirement-quotes-${id}`} />
        </Disclosure>
        <Disclosure summary="How the plan derived this" testId={`production-requirement-derivation-${id}`}>
          <DerivationInspector requirement={requirement} sceneId={sceneId} />
        </Disclosure>
      </div>
    </li>
  );
}

/**
 * The fields a person may change: the name, the category, the departments and
 * the approved count. Only what changed is sent.
 */
function RequirementEditForm({
  requirement,
  busy,
  onCancel,
  onSave,
}: {
  requirement: ProductionRequirementView;
  busy: boolean;
  onCancel: () => void;
  onSave: (edit: RequirementEdit) => void;
}) {
  const id = requirement.id;
  const [name, setName] = useState(requirement.name);
  const [category, setCategory] = useState<PlanCategory | ''>(requirement.primaryCategory ?? '');
  const [departments, setDepartments] = useState<readonly ProductionDepartment[]>(
    requirement.departments.map((d) => d.department),
  );
  const [count, setCount] = useState(requirement.approvedQuantity === null ? '' : String(requirement.approvedQuantity));

  const parsedCount = count.trim() === '' ? null : Number(count);
  const countValid = parsedCount === null || (Number.isInteger(parsedCount) && parsedCount >= 1 && parsedCount <= 9999);
  const valid = name.trim() !== '' && countValid;

  const save = () => {
    const edit: RequirementEdit = {};
    if (name.trim() !== requirement.name) edit.name = name.trim();
    const nextCategory = category === '' ? null : category;
    if (nextCategory !== requirement.primaryCategory) edit.primaryCategory = nextCategory;
    const before = requirement.departments.map((d) => d.department);
    if (before.length !== departments.length || before.some((d) => !departments.includes(d))) {
      // Kept departments keep their task; a new one starts with none.
      edit.departments = departments.map(
        (department): DepartmentTask =>
          requirement.departments.find((d) => d.department === department) ?? { department, task: '', primary: false },
      );
    }
    if (parsedCount !== requirement.approvedQuantity) edit.approvedQuantity = parsedCount;
    if (Object.keys(edit).length === 0) {
      onCancel();
      return;
    }
    onSave(edit);
  };

  return (
    <form
      className="pp-form"
      data-testid={`production-requirement-edit-form-${id}`}
      onSubmit={(event) => {
        event.preventDefault();
        if (valid) save();
      }}
    >
      <label className="pp-field">
        <span>Name</span>
        <input
          value={name}
          maxLength={255}
          data-testid={`production-requirement-edit-name-${id}`}
          onChange={(e) => {
            setName(e.target.value);
          }}
        />
      </label>
      <label className="pp-field">
        <span>Category</span>
        <select
          value={category}
          data-testid={`production-requirement-edit-category-${id}`}
          onChange={(e) => {
            setCategory(PLAN_CATEGORIES.find((c) => c === e.target.value) ?? '');
          }}
        >
          <option value="">No category</option>
          {PLAN_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {categoryLabel(c)}
            </option>
          ))}
        </select>
      </label>
      <label className="pp-field">
        <span>Approved count</span>
        <input
          type="number"
          min={1}
          max={9999}
          step={1}
          inputMode="numeric"
          value={count}
          placeholder="No approved count"
          data-testid={`production-requirement-edit-count-${id}`}
          aria-invalid={!countValid}
          onChange={(e) => {
            setCount(e.target.value);
          }}
        />
      </label>
      <fieldset className="pp-field pp-departments">
        <legend>Departments</legend>
        {DEPARTMENTS.map((department) => (
          <label key={department} className="pp-check">
            <input
              type="checkbox"
              checked={departments.includes(department)}
              data-testid={`production-requirement-edit-department-${id}-${department}`}
              onChange={(e) => {
                const on = e.target.checked;
                setDepartments((current) =>
                  on ? [...current, department] : current.filter((d) => d !== department),
                );
              }}
            />
            {DEPARTMENT_LABELS[department]}
          </label>
        ))}
      </fieldset>
      <div className="ui-toolbar">
        <button type="submit" className="ctl" disabled={!valid || busy} data-testid={`production-requirement-edit-save-${id}`}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="ctl" onClick={onCancel} data-testid={`production-requirement-edit-cancel-${id}`}>
          Cancel
        </button>
      </div>
    </form>
  );
}
